import { service } from '@ember/service';
import { trackedFunction } from 'reactiveweb/function';
import Service from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import type PartnerService from './partner';
import type SessionContextService from './session-context';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type AccountingTransactionModel from 'tio-common/models/accounting-transaction';

export interface PastContributionPayment {
  amount: number;
  line_item_date: string;
  line_item_period: string;
  line_item_type: string;
  transaction_date?: string | null;
  servicer: string;
}

export enum PaymentValidationErrorKey {
  ACCOUNTS_UNPAYABLE = 'ACCOUNTS_UNPAYABLE',
  NO_LOAN_BALANCE = 'NO_LOAN_BALANCE',
  EXCEEDED_LIMIT = 'EXCEEDED_LIMIT',
  NOT_STARTED_PLAN = 'NOT_STARTED_PLAN',
  NO_PLAN_CONTRIBUTION = 'NO_PLAN_CONTRIBUTION',
  INVALID_LOAN_TYPE = 'INVALID_LOAN_TYPE',
  AGGREGATION_INVALID_LOAN_TYPE = 'AGGREGATION_INVALID_LOAN_TYPE',
  NO_EMPLOYEE_TRANSACTIONS = 'NO_EMPLOYEE_TRANSACTIONS',
  TERMS_NOT_ACCEPTED = 'TERMS_NOT_ACCEPTED',
  STALE_ACCOUNT = 'STALE_ACCOUNT',
  NAME_MISMATCH = 'NAME_MISMATCH',
  INVALID_LOAN_STATUS = 'INVALID_LOAN_STATUS',
}

export interface PaymentValidationError {
  key: PaymentValidationErrorKey;
  message: string;
  source: string;
}

export interface FutureContributionPayment {
  amount: number;
  date: string;
  state: string;
  period: string;
  validation_errors: PaymentValidationError[];
}

export default class EmployeeService extends Service {
  @service declare partner: PartnerService;
  @service declare sessionContext: SessionContextService;
  @service declare store: StoreService;
  @service declare intl: IntlService;

  get employee() {
    return this.sessionContext.currentEmployee;
  }

  get current() {
    return this.sessionContext.currentEmployee;
  }

  get contributionPayments(): PastContributionPayment[] {
    return this.contributionsState.value?.payments || [];
  }

  get contributionSummary() {
    return this.contributionsState.value?.summary;
  }

  get futureContributionPayments(): FutureContributionPayment[] {
    return this.futurePayments.value || [];
  }

  get hasScholarships() {
    const scholarships = this.employee?.scholarships;

    if (scholarships?.length) {
      const validScholarship = scholarships.find((scholarship) => {
        if (!scholarship.thruDate) {
          return true;
        }
        const thruDate = new Date(scholarship.thruDate).getTime();
        const nowDate = new Date().getTime();
        return thruDate > nowDate;
      });
      return !!validScholarship;
    }

    return false;
  }

  get hasContributions() {
    return !!this.contributionPayments.length;
  }

  get isTaOnlyEmployee() {
    const companyKey = this.employee?.plan?.companyKey ?? '';
    const taOnlyPlanId = this.partner.companySettings?.tuitionAssistance?.taOnlyPlanId ?? '';
    return !!(taOnlyPlanId && companyKey.includes(taOnlyPlanId));
  }

  get isLoadedEmployee() {
    return !!this.employee?.id;
  }

  get needsToLinkLoans() {
    return !this.employee.needsToLinkLoans;
  }

  get hasRogueFein() {
    return this.employee.pslfProfile?.feinIsRogue;
  }

  get accountAlerts() {
    const person = this.employee?.person;
    if (!person) {
      return [];
    }
    const { accountReceivingContributions } = person;
    if (!accountReceivingContributions) {
      return [];
    }

    const alerts = [];

    if (!accountReceivingContributions.number || accountReceivingContributions.number === '') {
      alerts.push({ content: this.intl.t('slr.error.bad_account_number'), uploadDocument: true });
    }

    const hasLoanBalance = accountReceivingContributions.loans?.some((loan) => {
      return loan.currentBalance > 0;
    });
    if (!hasLoanBalance) {
      alerts.push({ content: this.intl.t('slr.error.no_loan_balance'), uploadDocument: true });
    }

    if (!accountReceivingContributions.postalAddress) {
      const showAddressSelect =
        accountReceivingContributions.financialInstitution?.servicerAddressType === 'MANUAL';
      alerts.push({
        content: this.intl.t('slr.error.no_address'),
        addressSelect: showAddressSelect,
        uploadDocument: !showAddressSelect,
      });
    }

    return alerts;
  }

  get documents() {
    return this.documentsData?.value || [];
  }

  get isPartiallyApplied() {
    return (
      !this.employee?.person?.accountReceivingContributions &&
      this.documents?.some((doc) => doc.reflectionState === 'ReflectionState.PARTIALLY_APPLIED')
    );
  }

  /**************************
   **  JSONAPI Query Methods **
   **************************/

  /**
   * Fetches contribution transactions via JSONAPI with pagination, sorting, and filtering.
   * Replaces the legacy adapter passthrough to tio-payments.
   *
   * Queries `accounting-transactions` filtered by employee, with the accounting-line-item
   * relationship included for period/type data.
   */
  async queryContributionTransactions(options?: {
    page?: { number?: number; size?: number };
    sort?: string;
  }) {
    const employee = this.employee;
    if (!employee?.id) {
      return { transactions: [] as AccountingTransactionModel[], meta: {} };
    }

    const query: Record<string, unknown> = {
      filter: { employee: employee.id },
      include: 'accounting-line-item',
      sort: options?.sort || '-transaction-date',
    };

    const pageNumber = options?.page?.number ?? 1;
    const pageSize = options?.page?.size ?? 6;
    query['page'] = {
      offset: (pageNumber - 1) * pageSize,
      limit: pageSize,
    };

    const transactions = await this.store.query<AccountingTransactionModel>(
      'accounting-transaction',
      // @ts-expect-error - offset pagination query shape not in LegacyResourceQuery
      query
    );

    const rawMeta = (transactions as unknown as { meta: Record<string, unknown> }).meta;

    return {
      transactions,
      // JSONAPI pagination meta is available on the array
      meta: rawMeta || {},
    };
  }

  /**************************
   **  Tracked Functions   **
   **************************/

  contributionsState = trackedFunction(this, async () => {
    const employee = this.employee;
    if (!employee) {
      return;
    }
    try {
      // @ts-expect-error - Ember Data adapter method not typed on MinimumAdapterInterface
      return await this.store.adapterFor('employee').getAccountingPaymentContributions(employee.id);
    } catch (e) {
      console.warn(e);
    }
  });

  futurePayments = trackedFunction(this, async () => {
    const employee = this.employee;
    if (employee.plan && employee.plan.cadence !== 'NEVER') {
      try {
        // @ts-expect-error - Ember Data adapter method not typed on MinimumAdapterInterface
        return await this.store.adapterFor('employee').getAccountingFuturePayments(employee.id);
      } catch (e) {
        console.warn(e);
      }
    } else {
      return;
    }
  });

  documentsData = trackedFunction(this, async () => {
    if (!this.employee?.person) {
      return;
    }
    if (this.employee.person.activeAccounts.length) {
      // only need to query for documents to check for partially applied if no active accounts exist
      return;
    }
    const query = {
      filter: { person: this.employee.person.id },
      sort: '-created-at',
      page: { limit: 10 },
    };
    // @ts-expect-error investigate
    return this.store.query<ObservableDocumentModel>('observable-document', query);
  });
}
